import React, { useState, useEffect } from "react";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import "./RegulationList.css"; // Tái sử dụng CSS chung của hệ thống

const PAGE_SIZE = 10;

const RegulationUserView = () => {
  const [regulations, setRegulations] = useState([]);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("General"); // Tab mặc định: General hoặc Personal
  const [currentPage, setCurrentPage] = useState(1);
  
  // State lưu trữ danh sách các Tên Công Ty đang được mở rộng (mặc định mở rộng tất cả)
  const [expandedCompanies, setExpandedCompanies] = useState([]);
  // State lưu trữ danh sách các ID của dòng Cha (Main Document) đang được mở rộng
  const [expandedParentIds, setExpandedParentIds] = useState([]);

  // 🔥 Gọi API mỗi khi Tab hoạt động thay đổi nhằm tối ưu hóa dữ liệu tải về theo đúng nghiệp vụ
  useEffect(() => {
    fetchRegulationsByTab();
  }, [activeTab]);

  // Thiết lập mặc định mở rộng các công ty khi dữ liệu tải xong
  useEffect(() => {
    if (regulations.length > 0) {
      const distinctCompanies = [
        ...new Set(regulations.map((r) => r.companyName || r.company || "Target Company")),
      ];
      setExpandedCompanies(distinctCompanies);
    }
  }, [regulations]);

  // Hàm hỗ trợ xử lý khi gặp lỗi 401 Unauthorized
  const handleUnauthorizedError = (error) => {
    if (error.response && error.response.status === 401) {
      console.error("❌ Session expired or User ID not found. Redirecting to login...");
      alert("Your session has expired. Please log in again!");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
      return true;
    }
    return false;
  };

  // 🔥 HÀM FETCH ĐÃ ĐƯỢC CHUYỂN ĐỔI GỌI THEO TỪNG ENDPOINT RIÊNG BIỆT CỦA CÁC TAB
  const fetchRegulationsByTab = async () => {
    try {
      let response;
      
      if (activeTab === "General") {
        // Gọi API General (Không cần check log, map type mặc định 'Decision' ở backend/frontend)
        response = await axiosClient.get(API_ENDPOINTS.general);
      } else {
        // Lấy userId hiện tại đang đăng nhập từ localStorage để check Personal log
        let currentUserId = 0;
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const userObj = JSON.parse(storedUser);
          currentUserId = userObj.id || userObj.UserId || 0;
        }

        // Gọi API Personal kèm tham số userId
        response = await axiosClient.get(API_ENDPOINTS.personal, {
          params: { userId: currentUserId }
        });
      }

      console.log(`[API Response] Data fetch for Tab ${activeTab}:`, response.data);
      setRegulations(response.data || []);
    } catch (error) {
      console.error(`❌ Lỗi load Regulations theo tab ${activeTab}:`, error);
      handleUnauthorizedError(error);
    }
  };

  // Hàm xử lý gọi API lưu log khi User CHỦ ĐỘNG ấn nút xác nhận đã đọc
  const handleMarkAsRead = async (id, title) => {
    try {
      let userId = 0;
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        userId = userObj.id || userObj.UserId || 0;
      }

      await axiosClient.post(API_ENDPOINTS.markRegulationAsRead(id), { userId: userId });
      
      setRegulations((prevRegulations) =>
        prevRegulations.map((item) =>
          item.id === id ? { ...item, isRead: true, hasRead: true } : item
        )
      );

      alert(`You have successfully confirmed reading: "${title}"`);
      fetchRegulationsByTab(); // 🔥 Re-fetch theo Tab hiện tại sau khi đọc xong
    } catch (error) {
      console.error("❌ Error marking document as read:", error);
      if (!handleUnauthorizedError(error)) {
        const serverMessage = error.response?.data?.message || "Failed to confirm read status!";
        alert(serverMessage);
      }
    }
  };

  const formatFileUrl = (url) => {
    if (!url) return "";
    const cleanUrl = url.replace(/\\/g, "/");
    let baseUrl = API_ENDPOINTS.url || axiosClient.defaults.baseURL || "";
    if (baseUrl.endsWith("/")) {
      baseUrl = baseUrl.slice(0, -1);
    }
    const cleanFileUrl = cleanUrl.startsWith("/") ? cleanUrl : "/" + cleanUrl;
    return cleanUrl.startsWith("http") ? cleanUrl : `${baseUrl}/api${cleanFileUrl}`;
  };

  // Đóng/mở rộng cây thư mục công ty ngoài cùng
  const toggleCompanyExpand = (companyName) => {
    setExpandedCompanies((prev) =>
      prev.includes(companyName)
        ? prev.filter((name) => name !== companyName)
        : [...prev, companyName]
    );
  };

  // Đóng/mở rộng văn bản gốc (Main Document)
  const toggleParentExpand = (parentId) => {
    setExpandedParentIds((prevIds) =>
      prevIds.includes(parentId)
        ? prevIds.filter((id) => id !== parentId)
        : [...prevIds, parentId]
    );
  };

  // --- XỬ LÝ LOGIC PHÂN NHÓM CÂY ĐỘNG THEO TỪNG CÔNG TY THỰC TẾ ---
  const getHierarchicalFilteredData = () => {
    const matchesSearch = (r) => {
      if (!search) return true;
      const searchLower = search.toLowerCase();
      return (
        (r.title || "").toLowerCase().includes(searchLower) ||
        (r.content || "").toLowerCase().includes(searchLower) ||
        (r.notes || "").toLowerCase().includes(searchLower)
      );
    };

    // Khớp chính xác với chuỗi Type được trả về từ API Backend mới (General hoặc Personal)
    const targetType = activeTab === "General" ? "General" : "Personal";
    const tabRegulations = regulations.filter((r) => (r.type || "").toLowerCase() === targetType.toLowerCase());

    // Lấy danh sách tên tất cả các công ty xuất hiện trong data (ví dụ Luxcom, Pex...)
    const distinctCompanies = [
      ...new Set(tabRegulations.map((r) => r.companyName || r.company || "Target Company")),
    ];

    const resultTree = [];

    distinctCompanies.forEach((compName) => {
      // Lọc toàn bộ văn bản thuộc về công ty này
      const compRegulations = tabRegulations.filter(
        (r) => (r.companyName || r.company || "Target Company") === compName
      );

      const parents = compRegulations.filter((r) => !r.parentId);
      const children = compRegulations.filter((r) => r.parentId);

      const companySubNodes = [];
      
      // Biến theo dõi xem toàn bộ công ty này có tài liệu nào chưa đọc hay không
      let companyHasUnread = false;

      parents.forEach((parent) => {
        const parentMatches = matchesSearch(parent);
        const itemChildren = children.filter((child) => String(child.parentId) === String(parent.id));
        const matchedChildren = itemChildren.filter(matchesSearch);

        if (parentMatches || matchedChildren.length > 0) {
          const isParentExpanded = expandedParentIds.includes(parent.id);

          // KIỂM TRA LOGIC TRẠNG THÁI ĐỌC CỦA CHA DỰA TRÊN CÁC CON THỰC TẾ
          const hasUnreadChild = itemChildren.some((child) => !child.isRead && !child.hasRead);
          
          if (hasUnreadChild) {
            companyHasUnread = true;
          }

          const parentRow = {
            ...parent,
            isParentRow: true,
            hasChildren: itemChildren.length > 0,
            isExpanded: isParentExpanded,
            level: 2,
            companyGroup: compName,
            parentIsUnread: itemChildren.length > 0 ? hasUnreadChild : false // 🔥 Đã sửa dấu = thành dấu : ở đây
          };

          const childRows = [];
          if (isParentExpanded) {
            matchedChildren.forEach((child) => {
              childRows.push({ ...child, isParentRow: false, level: 3, companyGroup: compName });
            });
          }

          companySubNodes.push({ parentRow, childRows });
        }
      });

      // Xử lý các dòng con mồ côi theo từng công ty
      const orphanRows = [];
      children.forEach((child) => {
        const parentExists = parents.some((p) => String(p.id) === String(child.parentId));
        if (!parentExists && matchesSearch(child)) {
          orphanRows.push({ ...child, isParentRow: false, level: 2, companyGroup: compName });
          if (!child.isRead && !child.hasRead) {
            companyHasUnread = true;
          }
        }
      });

      // Nếu công ty có dữ liệu hiển thị, đẩy node Công Ty ngoài cùng vào cây (Level 1)
      if (companySubNodes.length > 0 || orphanRows.length > 0) {
        const isCompExpanded = expandedCompanies.includes(compName);
        
        resultTree.push({
          id: `COMP_NODE_${compName}`,
          isCompanyRow: true,
          title: compName, 
          content: "Corporate Unit Group",
          hasChildren: true,
          isExpanded: isCompExpanded,
          level: 1,
          companyGroup: compName,
          companyIsUnread: companyHasUnread // 🔥 Lưu trạng thái chưa đọc của toàn bộ công ty
        });

        // Nếu lớp vỏ công ty đang được mở rộng, render tiếp các node con bên trong
        if (isCompExpanded) {
          companySubNodes.forEach((node) => {
            resultTree.push(node.parentRow);
            node.childRows.forEach((child) => resultTree.push(child));
          });
          orphanRows.forEach((orphan) => {
            resultTree.push(orphan);
          });
        }
      }
    });

    return resultTree;
  };

  const getReadStats = () => {
    const targetType = activeTab === "General" ? "General" : "Personal";
    const currentTabDocs = regulations.filter((r) => (r.type || "").toLowerCase() === targetType.toLowerCase() && !r.isParentRow && r.parentId);
    const readCount = currentTabDocs.filter((r) => r.isRead || r.hasRead).length;
    const unreadCount = currentTabDocs.length - readCount;
    return { total: currentTabDocs.length, read: readCount, unread: unreadCount };
  };

  const stats = getReadStats();
  const filteredTreeData = getHierarchicalFilteredData();

  const totalPages = Math.ceil(filteredTreeData.length / PAGE_SIZE);
  const currentData = filteredTreeData.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const thStyle = { backgroundColor: "#f8f9fc", color: "#4e73df", fontWeight: "bold", fontSize: "0.8rem", textTransform: "uppercase" };

  return (
    <div className="container-fluid py-4">
      {/* Bootstrap Nav Tabs phân chia giữa General và Personal */}
      <ul className="nav nav-tabs border-bottom-0 mb-n1" style={{ position: "relative", zIndex: 1 }}>
        <li className="nav-item">
          <button 
            className={`nav-link px-4 fw-bold ${activeTab === "General" ? "active bg-white border-bottom-white text-primary" : "text-muted bg-light"}`}
            style={{ borderRadius: "8px 8px 0 0", border: "1px solid #dee2e6", borderBottom: activeTab === "General" ? "1px solid #fff" : "" }}
            onClick={() => {
              setActiveTab("General");
              setCurrentPage(1);
              setExpandedParentIds([]);
            }}
          >
            <i className="fas fa-gavel me-2"></i>General
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link px-4 fw-bold ${activeTab === "Personal" ? "active bg-white border-bottom-white text-primary" : "text-muted bg-light"}`}
            style={{ borderRadius: "8px 8px 0 0", border: "1px solid #dee2e6", borderBottom: activeTab === "Personal" ? "1px solid #fff" : "" }}
            onClick={() => {
              setActiveTab("Personal");
              setCurrentPage(1);
              setExpandedParentIds([]);
            }}
          >
            <i className="fas fa-book me-2"></i>Personal
          </button>
        </li>
      </ul>

      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap gap-3" style={{ borderRadius: "0 8px 0 0" }}>
          <div>
            <h5 className="m-0 font-weight-bold text-dark text-uppercase mb-2" style={{ fontSize: "1rem" }}>
              {activeTab === "General" ? "General Documents View" : "Personal Documents View"}
            </h5>
            <div className="d-flex gap-2 flex-wrap">
              <span className="badge bg-light text-dark border px-2 py-1.5" style={{ fontSize: "0.75rem" }}>
                <i className="fas fa-list text-secondary me-1"></i> Total: <strong>{stats.total}</strong>
              </span>
              <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1.5" style={{ fontSize: "0.75rem" }}>
                <i className="fas fa-check-circle me-1"></i> Read: <strong>{stats.read}</strong>
              </span>
              <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1.5" style={{ fontSize: "0.75rem" }}>
                <i className="fas fa-exclamation-circle me-1"></i> Unread: <strong>{stats.unread}</strong>
              </span>
            </div>
          </div>
          
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <div className="position-relative">
               <input
                type="text"
                className="form-control form-control-sm ps-4"
                style={{ width: "220px", borderRadius: "20px" }}
                placeholder={`Search ${activeTab.toLowerCase()} documents...`}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <i className="fas fa-search position-absolute text-muted" style={{ left: "12px", top: "8px", fontSize: "12px" }}></i>
            </div>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th className="text-center" style={{ ...thStyle, width: "120px" }}>No. / Ver</th>
                  <th style={thStyle}>Classification</th>
                  <th style={thStyle}>Title & Content</th>
                  <th style={thStyle}>Effective Date</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Attachment Actions</th>
                  <th className="text-center" style={{ ...thStyle, width: "110px" }}>Read Status</th>
                  <th className="text-center" style={{ ...thStyle, width: "150px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length > 0 ? (
                  currentData.map((r) => {
                    let rowStyle = {};
                    let rowClass = "";
                    
                    if (r.isCompanyRow) {
                      rowClass = "table-primary fw-bold text-primary";
                      rowStyle = { borderLeft: "5px solid #4e73df", backgroundColor: "#f4f6fd" };
                    } else if (r.isParentRow) {
                      rowClass = "bg-light-subtle table-light";
                      rowStyle = { borderLeft: "4px solid #858796" };
                    }

                    return (
                      <tr key={r.id} className={rowClass} style={rowStyle}>
                        {/* Cột 1: Đóng/Mở rộng cây + Vẽ đường Line dọc */}
                        <td className="text-muted small fw-bold">
                          <div className="d-flex align-items-center justify-content-start ps-2 gap-1">
                            {r.level === 2 && (
                              <span className="text-primary opacity-50 font-weight-normal me-2 ms-1" style={{ fontSize: "1.1rem", marginTop: "-6px" }}>│</span>
                            )}
                            {r.level === 3 && (
                              <>
                                <span className="text-primary opacity-50 font-weight-normal ms-1" style={{ fontSize: "1.1rem", marginTop: "-6px" }}>│</span>
                                <span className="text-muted opacity-30 font-weight-normal me-2 ms-2" style={{ fontSize: "1.1rem", marginTop: "-6px" }}>│</span>
                              </>
                            )}

                            {r.isCompanyRow ? (
                              <button
                                type="button"
                                className={`btn btn-xs btn-link p-0 text-decoration-none fw-bold ${r.isExpanded ? "text-danger" : "text-primary"}`}
                                style={{ fontSize: "0.95rem", width: "18px", lineHeight: "1" }}
                                onClick={() => toggleCompanyExpand(r.title)}
                                title={r.isExpanded ? "Collapse Company" : "Expand Company"}
                              >
                                <i className={`fas ${r.isExpanded ? "fa-minus-square" : "fa-plus-square"}`}></i>
                              </button>
                            ) : r.isParentRow ? (
                              r.hasChildren ? (
                                <button
                                  type="button"
                                  className={`btn btn-xs btn-link p-0 text-decoration-none fw-bold ${r.isExpanded ? "text-danger" : "text-primary"}`}
                                  style={{ fontSize: "0.85rem", width: "18px", lineHeight: "1" }}
                                  onClick={() => toggleParentExpand(r.id)}
                                  title={r.isExpanded ? "Collapse revisions" : "Expand revisions"}
                                >
                                  <i className={`fas ${r.isExpanded ? "fa-minus-square" : "fa-plus-square"}`}></i>
                                </button>
                              ) : (
                                <span style={{ width: "18px" }}></span>
                              )
                            ) : (
                              <span style={{ width: "18px" }}></span>
                            )}

                            {/* Icon thư mục / tòa nhà */}
                            {r.isCompanyRow ? (
                              <i className="fas fa-building text-primary" style={{ fontSize: "1rem" }} title="Company Unit"></i>
                            ) : r.isParentRow ? (
                              <i className="fas fa-folder text-warning opacity-90" style={{ fontSize: "0.95rem" }} title="Main Document"></i>
                            ) : (
                              <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle px-1.5 py-0.5" style={{ fontSize: "0.7rem" }}>
                                {r.version ? `v${r.version}` : "v1.0"}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Cột 2: Classification */}
                        <td>
                          {!r.isCompanyRow && (
                            <span className={`badge border px-2 py-1 text-dark fw-bold ${r.type === "General" || r.type === "Decision" ? "bg-light border-purple" : "bg-light border-info"}`}>
                              {r.type}
                            </span>
                          )}
                          {r.isCompanyRow && <span className="badge bg-primary text-white">COMPANY</span>}
                        </td>

                        {/* Cột 3: Title & Content */}
                        <td>
                          <div className={`text-dark ${r.isCompanyRow ? "text-uppercase fw-bold text-primary" : r.isParentRow ? "fw-bold text-uppercase" : "ps-2"}`}>
                            {r.isCompanyRow ? <strong>{r.title}</strong> : r.title}
                          </div>
                          <small className="text-muted text-truncate d-block" style={{ maxWidth: "400px" }}>
                            {r.content}
                          </small>
                        </td>

                        {/* Cột 4: Effective Date */}
                        <td className="small">
                          {r.effectiveDate ? new Date(r.effectiveDate).toLocaleDateString("en-GB") : "---"}
                        </td>

                        {/* Cột 5: Status */}
                        <td>
                          {!r.isCompanyRow && (
                            <span className={`badge px-2 py-1 ${r.status === "Public" ? "bg-success" : "bg-warning text-dark"}`}>
                              {r.status || "Active"}
                            </span>
                          )}
                        </td>

                        {/* Cột 6: Attachment Actions */}
                        <td>
                          {r.documentUrl ? (
                            <div className="d-flex gap-2">
                              <a 
                                href={(url => {
          console.log("URL đang được sử dụng:", url);
          return url;
        })(formatFileUrl(r.documentUrl))}
                                download={`Document_${r.id || new Date().getTime()}`}
                                className="btn btn-xs btn-outline-success px-2 py-1 shadow-sm d-flex align-items-center gap-1 text-decoration-none fw-bold"
                                style={{ fontSize: "0.7rem", borderRadius: "4px" }}
                              >
                                <i className="fas fa-download"></i> Download File
                              </a>
                            </div>
                          ) : (
                            <span className="text-muted small">---</span>
                          )}
                        </td>
                        
                        {/* Cột 7: Read Status */}
                        <td className="text-center">
                          {r.isCompanyRow ? (
                            /* Nếu lớp ngoài cùng (Company) có tài liệu con chưa đọc -> Hiện Unread đỏ */
                            r.companyIsUnread ? (
                              <span className="badge bg-danger-subtle text-danger border border-danger px-2 py-1" style={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                                <i className="fas fa-envelope me-1"></i> Unread
                              </span>
                            ) : (
                              <span className="badge bg-success-subtle text-success border border-success px-2 py-1" style={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                                <i className="fas fa-envelope-open me-1"></i> Read
                              </span>
                            )
                          ) : r.isParentRow ? (
                            /* Nếu là dòng CHA (Main Document) */
                            r.hasChildren ? (
                              r.parentIsUnread ? (
                                <span className="badge bg-danger-subtle text-danger border border-danger px-2 py-1" style={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                                  <i className="fas fa-envelope me-1"></i> Unread
                                </span>
                              ) : (
                                <span className="badge bg-success-subtle text-success border border-success px-2 py-1" style={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                                  <i className="fas fa-envelope-open me-1"></i> Read
                                </span>
                              )
                            ) : (
                              <span className="text-muted small italic">---</span>
                            )
                          ) : r.level === 3 ? (
                            /* Nếu là dòng CON (Revisions) */
                            r.isRead || r.hasRead ? (
                              <span className="badge bg-success-subtle text-success border border-success px-2 py-1" style={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                                <i className="fas fa-envelope-open me-1"></i> Read
                              </span>
                            ) : (
                              <span className="badge bg-danger-subtle text-danger border border-danger px-2 py-1" style={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                                <i className="fas fa-envelope me-1"></i> Unread
                              </span>
                            )
                          ) : (
                            <span className="text-muted small italic">---</span>
                          )}
                        </td>

                        {/* Cột 8: Action */}
                        <td className="text-center">
                          {r.level === 3 ? (
                            <button
                              type="button"
                              className={`btn btn-xs px-2 py-1 rounded-pill shadow-sm fw-bold ${r.isRead || r.hasRead ? "btn-success disabled" : "btn-outline-success"}`}
                              style={{ fontSize: "0.7rem" }}
                              onClick={() => handleMarkAsRead(r.id, r.title)}
                              disabled={r.isRead || r.hasRead}
                              title={r.isRead || r.hasRead ? "You have already acknowledged this document" : "Click to confirm you have read and understood this document"}
                            >
                              <i className={`fas ${r.isRead || r.hasRead ? "fa-check-double" : "fa-check-circle"} me-1`}></i> 
                              {r.isRead || r.hasRead ? "Acknowledged" : "Confirm Read"}
                            </button>
                          ) : (
                            <span className="text-muted small italic">
                              {r.isCompanyRow ? "Corp Node" : "Main Doc"}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-5 text-muted">
                      <i className="fas fa-folder-open d-block mb-2 fa-2x"></i>
                      No records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="d-flex justify-content-between align-items-center p-3 bg-light border-top">
            <span className="small text-muted fw-bold">
              Showing page {currentPage} of {totalPages || 1}
            </span>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button type="button" className="page-link border-0 shadow-sm mx-1 rounded-circle" onClick={() => setCurrentPage(currentPage - 1)}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                </li>
                {[...Array(totalPages)].map((_, i) => (
                  <li key={i} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                    <button 
                      type="button"
                      className={`page-link border-0 shadow-sm mx-1 rounded-circle ${currentPage === i + 1 ? "bg-primary text-white" : "bg-white text-dark"}`} 
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button type="button" className="page-link border-0 shadow-sm mx-1 rounded-circle" onClick={() => setCurrentPage(currentPage + 1)}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegulationUserView;